import { useContext, useEffect, useEffectEvent, useSyncExternalStore } from 'react';

import { RealtimeContext } from './context';
import type { RealtimeEnvelope, RealtimeManager, RealtimeStatus } from './manager';

export function useRealtime(): RealtimeManager {
  const manager = useContext(RealtimeContext);
  if (!manager) throw new Error('useRealtime() requires <RealtimeProvider>.');
  return manager;
}

/** Connection status for banners / indicators. */
export function useRealtimeStatus(): RealtimeStatus {
  const manager = useRealtime();
  return useSyncExternalStore(manager.subscribeStatus, manager.getStatus, manager.getStatus);
}

/**
 * Runs `handler` for every realtime event of `type` (exact, `prefix.*` or `*`) while mounted.
 * The handler always sees the latest props/state without re-subscribing.
 */
export function useRealtimeEvent<TPayload = unknown>(
  type: string,
  handler: (payload: TPayload, envelope: RealtimeEnvelope<TPayload>) => void,
): void {
  const manager = useRealtime();
  const onEvent = useEffectEvent((payload: TPayload, envelope: RealtimeEnvelope<TPayload>) =>
    handler(payload, envelope),
  );
  useEffect(
    () => manager.on<TPayload>(type, (payload, envelope) => onEvent(payload, envelope)),
    [manager, type],
  );
}

/** Subscribes to a server topic (e.g. `chat:{id}`) while mounted; pass null to skip. */
export function useTopic(topic: string | null | undefined): void {
  const manager = useRealtime();
  useEffect(() => (topic ? manager.subscribe(topic) : undefined), [manager, topic]);
}
